import React, { useMemo } from 'react';

import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { normalizeOptionalText } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { FC_TRANSFER_DISPLAY_FALLBACK } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';

const labelClassName = 'text-left text-xs font-semibold uppercase leading-tight tracking-wide text-muted-foreground';
const valueClassName = 'text-left text-sm font-medium text-foreground';

/**
 * Receive-focused summary: facilities, created metadata, line count, optional receipt timestamps.
 *
 * @param {Object} props
 * @param {Record<string, unknown> | null} [props.transfer=null]
 */
export function AcceptTransferSummaryCard({ transfer = null }) {
    const sections = useMemo(() => {
        const items = Array.isArray(transfer?.items) ? transfer.items : [];
        const linesDisplay = String(items.length);

        const createdRaw = transfer?.createdAt ?? transfer?.created_at;
        const createdDisplay = formatPurchaseOrderDate(createdRaw, FC_TRANSFER_DISPLAY_FALLBACK);

        const receiptStartedRaw = transfer?.receiptStartedAt ?? transfer?.receipt_started_at;
        const finalizedRaw = transfer?.finalizedAt ?? transfer?.finalized_at;

        const base = [
            {
                label: 'Source FC',
                value: normalizeOptionalText(transfer?.sourceFacilityName ?? transfer?.source_facility_name),
            },
            {
                label: 'Destination FC',
                value: normalizeOptionalText(transfer?.destinationFacilityName ?? transfer?.destination_facility_name),
            },
            { label: 'Created By', value: normalizeOptionalText(transfer?.createdBy ?? transfer?.created_by) },
            { label: 'Created On', value: createdDisplay },
            { label: 'Total lines', value: linesDisplay },
        ];

        const optional = [];
        if (receiptStartedRaw !== undefined && receiptStartedRaw !== null && receiptStartedRaw !== '') {
            optional.push({
                label: 'Receipt started',
                value: formatPurchaseOrderDate(receiptStartedRaw, FC_TRANSFER_DISPLAY_FALLBACK),
            });
        }
        if (finalizedRaw !== undefined && finalizedRaw !== null && finalizedRaw !== '') {
            optional.push({
                label: 'Finalized',
                value: formatPurchaseOrderDate(finalizedRaw, FC_TRANSFER_DISPLAY_FALLBACK),
            });
        }

        return [...base, ...optional];
    }, [transfer]);

    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4">
                <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">Summary</h2>
                <dl className="flex flex-nowrap items-start gap-x-8 gap-y-2 overflow-x-auto pb-1">
                    {sections.map(({ label, value }) => (
                        <div key={label} className="flex min-w-[7.5rem] shrink-0 flex-col gap-1">
                            <dt className={labelClassName}>{label}</dt>
                            <dd className={`${valueClassName} break-words`}>{value}</dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    );
}

AcceptTransferSummaryCard.displayName = 'AcceptTransferSummaryCard';
